import {createSelector} from 'reselect';
import get from 'lodash/get';

import {toURL} from '@homeaway/search-url-parser';

import {getListing, getListingPage, getTranslations, getTravelerFeatures} from './store';
import {labelMaker} from '../../server/utils/labelMaker';
import {getJsReviews} from './reviews';
import {
    getAverageNightly,
    getTotals,
    getPriceSummaryDetails,
    getPriceSummarySecondaryDetails
} from './priceDetails';
import {getTripNights} from './tripNights';
import {getIsMobile, getBrandName} from './system';
import {getInstallmentsBadge, getNldBadge} from './badges';
import {getSearchUrlBaseParams} from './searchUrl';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import getUnitId from '../utils/getUnitId';
import {
    getBookingType,
    getGeography,
    getListingIds,
    getListingHeadline,
    getMultiUnit,
    getPriceSummary
} from './listing';
import {getPremierBadge} from './badges';
import {areBothDatesValid} from '../utils/dateHelper';
import {getPriceSummaryObjectByType} from '../utils/pricingHelper';

const PRICE_SUMMARY_TYPE_PER_NIGHT = 'PER_NIGHT';

const getBookingAssistance = createSelector(
    [getBookingType, getTranslations, getIsMobile, getBrandName],
    (bookingType, translations, isMobile, brandName) => {
        const {payPerBooking, onlineBookable} = bookingType;
        const {bookingAssistNum, bookingAssistPPSNum} = translations;

        let number = bookingAssistNum;
        if (!payPerBooking && onlineBookable) {
            number = bookingAssistPPSNum;
        }

        return {number, brand: brandName, isMobile};
    }
);

/* eslint-disable complexity */
const getPrice = createSelector(
    [
        getAverageNightly,
        getTotals,
        getTripNights,
        getTranslations,
        getTravelerFeatures,
        getListing,
        getListingPage,
        getPriceSummary,
        getPriceSummaryDetails,
        getPriceSummarySecondaryDetails
    ],
    (
        averageNightly,
        totals,
        tripNights,
        translations,
        travelerFeatures,
        listing,
        listingPage,
        priceSummaryDefault,
        priceSummary,
        priceSummarySecondary
    ) => {
        let {amount, label, isPrimary: isNightlyPrimary} = getPriceSummaryObjectByType(
            priceSummary,
            priceSummarySecondary,
            PRICE_SUMMARY_TYPE_PER_NIGHT
        );
        let nightlyPrice = amount;
        let nightlyLabel = label;

        const pricingKey = listingPage.pricingKey;
        const priceSummaryEventParams = {
            unituuid: getUnitId(listing.unitApiUrl, false),
            eventstartdate: pricingKey.get('arrival'),
            eventenddate: pricingKey.get('departure'),
            eventnumadults: (pricingKey.get('adults') || 0).toString(),
            eventnumchildren: (pricingKey.get('children') || 0).toString(),
            eventpets: (pricingKey.get('pets') || false).toString(),
            eventcurrency: pricingKey.get('currency')
        };

        const validDates = areBothDatesValid(
            pricingKey.get('arrival'),
            pricingKey.get('departure')
        );

        if (validDates) {
            if (travelerFeatures.showTotal) {
                const totalAmount = totals.totalWithoutDeposits;

                if (totalAmount) {
                    amount = totalAmount;
                    label = labelMaker(translations.nightTotal, tripNights);
                }
            } else if (!amount || !label) {
                const nightlyAmount = travelerFeatures.includeTaxesAndFees
                    ? averageNightly.averageNightlyTotalTrip
                    : averageNightly.averageNightlyTotalRent;

                if (nightlyAmount) {
                    amount = nightlyAmount;
                    label = translations.perNight;
                    nightlyPrice = amount;
                    nightlyLabel = label;
                }
            }
        }
        if ((!amount || !label) && priceSummaryDefault) {
            amount = priceSummaryDefault.formattedAmount;
            label = priceSummaryDefault.pricePeriodDescription;
            nightlyPrice = amount;
            nightlyLabel = label;
        }

        // merge the final price and description into the EDAP event payload
        priceSummaryEventParams.stramount = amount;
        priceSummaryEventParams.perioddescription = label;

        return {
            amount,
            label,
            priceSummaryEventParams,
            isNightlyPrimary,
            nightlyPrice,
            nightlyLabel
        };
    }
);

const getLocation = createSelector(
    [getGeography],
    (geography) => {
        return get(geography, 'description') || null;
    }
);

const getLocationBreadcrumbs = createSelector(
    [getLocation, getSearchUrlBaseParams],
    (location, searchUrlBaseParams) => {
        if (!location) {
            return [];
        }

        const getSearchUrl = (locationArray, startIndex) => {
            const subArray = locationArray.slice(startIndex);

            return toURL({
                ...searchUrlBaseParams,
                q: subArray.join(', ')
            });
        };

        const locationArray = location.split(',').map((el) => el.trim());

        // add search links to the array
        // show max of three breadcrumbs
        // sort from large to small geo area
        const locationBreadcrumbs = locationArray
            .map((el, i) => {
                return {
                    name: el,
                    url: getSearchUrl(locationArray, i)
                };
            })
            .slice(0, 3)
            .reverse();

        return locationBreadcrumbs;
    }
);

const getReviewCount = createSelector(
    [getJsReviews],
    (reviewsData) => {
        return reviewsData.reviewCount;
    }
);

const getHeadline = createSelector(
    [getListingHeadline, getMultiUnit],
    (listingHeadline, multiUnit) => {
        const headline =
            multiUnit.isMultiUnit && multiUnit.unitName
                ? `${listingHeadline} - ${multiUnit.unitName}`
                : listingHeadline;

        return headline;
    }
);

const getListingSummary = createSelector(
    [
        getBookingAssistance,
        getIsMobile,
        getPrice,
        getLocationBreadcrumbs,
        getLocation,
        getListingPage,
        getPremierBadge,
        getTranslations,
        getHeadline,
        getBookingType,
        getListingIds,
        getMultiUnit,
        getInstallmentsBadge,
        getNldBadge,
        getTravelerSegmentFeatures
    ],
    (
        bookingAssistance,
        isMobile,
        price,
        locationBreadcrumbs,
        location,
        listingPage,
        premierBadge,
        translations,
        headline,
        bookingType,
        listingIds,
        multiUnit,
        installmentsBadge,
        nldBadge,
        travelerSegmentFeatures
    ) => {
        let propertyNum = labelMaker(translations.propertyNum, {propertynumber: ''});

        const labels = {
            averageNightly: translations.averageNightly,
            perNight: translations.perNight,
            map: translations.navMap,
            priceLabel: price.label,
            nightlyLabel: price.nightlyLabel,
            propertyNum,
            unitNum: translations.unitNum,
            skipToBookingDetails: translations.skipToBookingDetails,
            skipToBookingDetailsAnchour: translations.skipToBookingDetailsAnchour
        };

        const {showBackToSerp} = travelerSegmentFeatures;
        const isPremier = !!(premierBadge && premierBadge.name);

        return {
            bookingAssist: bookingAssistance,
            headline,
            location,
            locationBreadcrumbs,
            premier: premierBadge,
            propertyOlb: bookingType.onlineBookable,
            loadingRate: listingPage.loadingRate,
            multiUnit: multiUnit.isMultiUnit,
            listingId: listingIds.listingId,
            propertyId: listingIds.propertyId,
            unitNumber: multiUnit.unitNumber,
            price: price.amount,
            nightlyPrice: price.nightlyPrice,
            priceSummaryEventParams: price.priceSummaryEventParams,
            installmentsBadge,
            nldBadge,
            labels,
            showLocationBreadcrumbs: !showBackToSerp,
            showShareButtons: !isMobile,
            isPremier,
            isNightlyPrimary: price.isNightlyPrimary
        };
    }
);

export default getListingSummary;
export {
    getListingSummary,
    getLocationBreadcrumbs,
    getLocation,
    getBookingAssistance,
    getPrice,
    getReviewCount,
    getHeadline,
    PRICE_SUMMARY_TYPE_PER_NIGHT
};
